import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  useTheme,
  Popper,
  Paper,
  ClickAwayListener,
  Grid,
  Pagination,
  IconButton,
  Divider,
} from "@mui/material";
import dayjs from "dayjs";
import NotificationItem from "./NotificationItem";
import Card from "../../common/Card";
import { Close, Delete, MarkChatRead } from "@mui/icons-material";
import Button from "../../common/Button";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, showHideLoadingDialog, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { acknowledgeAllNotifications, addNewNotification, clearAllNotifications, composeNotification, deleteAllNotifications, fetchNotificationList, notificationAttachmentDTO, NotificationListDTO, resetNotificationData, useCreateNotificationSlice } from "../../../redux/slice/inbox/createNotificationSlice";
import { selectTokenValues, useAuthSlice } from "../../../redux/slice/authSlice";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useNavigation } from "../../../hooks/useNavigation";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import { useLocation } from "react-router-dom";
import { useModalStyles } from "../../common/useModalStyles";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

interface Attachment {
  documentName: string;
  content: string;
  fileType: string;
}

export interface NotificationData {
  notification: NotificationListDTO;
  notificationId: number;
  entityId: number;
  entity: string;
  title: string;
  description: string;
  time: string;
  date: string;
  formattedTime: string;
  formattedDate: string;
  acknowledged: boolean;
  hasEntity: boolean;
  isAssignable: boolean;
  attachments: notificationAttachmentDTO[]
  createdDate?: string;
  createdTime?: string;
  ackedByUserName?: string;
}

const NotificationList: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const { primaryBtnSx, cancelBtnSx, tokens: mTokens, isDark } = useModalStyles();

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const deleteAllBtnSx = {
    ...cancelBtnSx,
    fontSize: "0.86rem",
    py: 1.05,
    px: 1.6,
    whiteSpace: "nowrap" as const,
    backgroundColor: isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.04)",
    borderColor: "rgba(239,68,68,0.55)",
    color: mTokens.error,
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
    "&:hover": {
      color: "#fff",
      borderColor: mTokens.error,
      backgroundColor: mTokens.error,
      boxShadow: "0 6px 22px rgba(239,68,68,0.28)",
      "& .MuiTypography-root": { color: "#fff" },
      "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
    },
    "&.Mui-disabled": {
      opacity: 0.45,
      color: mTokens.error,
      borderColor: "rgba(239,68,68,0.35)",
      backgroundColor: isDark ? "rgba(239,68,68,0.04)" : "rgba(239,68,68,0.02)",
    },
  };


  const closeIconBtnSx = {
    width: 36,
    height: 36,
    borderRadius: "10px",
    color: mTokens.textSecondary,
    transition: "color 0.2s ease, background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      color: mTokens.error,
      backgroundColor: isDark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.1)",
      transform: "translateY(-2px)",
      boxShadow: "0 6px 14px rgba(239,68,68,0.3)",
    },
    "&:active": { transform: "translateY(0) scale(0.94)" },
  };

  const tabBarSx = {
    minHeight: 30,
    p: 0.3,
    borderRadius: "999px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)",
    width: "fit-content",
    maxWidth: "100%",
    "& .MuiTabs-indicator": { display: "none" },
    "& .MuiTabs-flexContainer": { gap: 0.25 },
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 700,
      fontSize: "0.76rem",
      minHeight: 26,
      height: 26,
      minWidth: { xs: 76, sm: 92 },
      borderRadius: "999px",
      color: mTokens.textSecondary,
      px: 1.4,
      py: 0,
      "&.Mui-selected": {
        color: "#fff",
        background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
        boxShadow: "0 2px 8px rgba(13,148,136,0.35)",
      },
    },
  };

  const paginationSx = {
    "& .MuiPaginationItem-root": {
      borderColor: listBorderColor,
      color: mTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: mTokens.accent,
        borderColor: mTokens.accent,
      },
    },
  };

  const initialTab = location.state?.notificationType === "acknowledged" ? 1 : 0;

  const [tab, setTab] = React.useState<number>(initialTab);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [showPopup, setShowPopup] = React.useState(false);
  const [composeMode, setComposeMode] = React.useState<"group" | "user" | null>(null);
  const notifications = useSelector(
    (state: RootState) => state.createNotificationSlice.notifications
  );
  const { userInfo } = useAuthSlice();
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { NotificationListInfo } = useCreateNotificationSlice();
  const [inboxData, setInboxData] = React.useState<NotificationData[]>([]);
  const [isUnread, setIsUnread] = React.useState<boolean>(true);
  const [totalPages, setToatalPages] = React.useState<number>(1);
  const [totalElements, setTotalElements] = React.useState<number>(1);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [showOptionPopup, setShowOptionsPopup] = React.useState<OptionPopupObjectProps>({ message: '', show: false });
  const accessibleNotifications = useSelector((state: RootState) => selectTokenValues("canAccessNotifications")(state));

  const open = Boolean(anchorEl);

  const ITEMS_PER_PAGE = 10;

    useEffect(() => {
      if (location?.state?.notificationType) {
        setTab(location?.state?.notificationType === 'unread' ? 0 : 1);
      }
    }, [location?.state?.notificationType]);

  useEffect(() => {
    if (!notifications || notifications[0].senderId != null) return;
    if (notifications.length > 0) {
      if (!(notifications[0].notificationAckId) && notifications[0].senderId == null) {
        dispatch(addNewNotification(notifications[0]));
        const newTotal = totalElements + 1;
        setTotalElements(newTotal);
      } else if (notifications[0].notificationAckId) {
        if (notifications[0].deleteAll === false || notifications[0].deleteAll === true) {
          refreshPage();
          let alertMessage = "";
          if (notifications[0].deleteAll === false) {
            if (notifications[0].deletedUserId === userInfo.userId) {
              alertMessage = "Notification has been premanently deleted";
            } else {
              alertMessage = "A Notification has been premanently deleted by another user";
            }
          } else {
            if (notifications[0].deletedUserId === userInfo.userId) {
              alertMessage = "All acknowledged notifications are cleared";
            } else {
              alertMessage = "All acknowledged notifications have been cleared by another user";
            }
          }

          const alertObj: SingleAlertInfo = {
            message: alertMessage,
            alertType: "success",
          };

          dispatch(setSingleAlertObj(alertObj));
        }
        const isAckOnceByOtherUser = notifications[0].ackUsrId !== userInfo.userId && notifications[0].ackType === "ACK_ONCE";
        if ((typeof notifications[0].ackUsrId !== 'undefined') && isAckOnceByOtherUser) {
          refreshPage();
          let alertMessage = ""
          if (notifications[0].ackedAll) {
            alertMessage = "All group messages have been acknowledged by another user";
          } else {
            alertMessage = "A group message has been acknowledged by another user";
          }

          const alertObj: SingleAlertInfo = {
            message: alertMessage,
            alertType: "success",
          };

          dispatch(setSingleAlertObj(alertObj));
        }
      }
    }
  }, [notifications])

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  useEffect(() => {
    setCurrentPage(1);
    if (tab === 0) {
      setIsUnread(true);
    } else {
      setIsUnread(false);
    }
  }, [tab])

  useEffect(() => {
    fetchInitialNotificationList();
  }, [isUnread, currentPage]);

  const refreshPage = () => {
    fetchInitialNotificationList();
  };

  const fetchInitialNotificationList = async () => {
    dispatch(showHideLoadingDialog(true));
    const responseNotifs = await dispatch(fetchNotificationList({
      notificationTypes: accessibleNotifications ? accessibleNotifications : [],
      unread: isUnread,
      notificationCategory: 'system',
      currentPage: currentPage,
      itemsPerPage: 10
    }));
    dispatch(showHideLoadingDialog(false));
  }

  useEffect(() => {
    if (!NotificationListInfo || (NotificationListInfo.content[0] && NotificationListInfo.content[0].senderId != null)) return;

    setToatalPages(NotificationListInfo.totalPages);
    setTotalElements(NotificationListInfo.totalElements);

    const formatAuditTimestamp = (timestamp: string) => {
      if (!timestamp) return { date: "", time: "" };

      const localTime = dayjs.utc(timestamp).local();

      if (localTime.isToday() || localTime.isYesterday()) {
        return {
          date: "",
          time: localTime.format("hh:mm A"),
        };
      } else {
        return {
          date: localTime.format("MMM D"),
          time: "",
        };
      }
    };

    const mappedData: NotificationData[] = NotificationListInfo.content.map((item: any) => {
      let date = "";
      let time = "";
      let createdDate = "";
      let createdTime = "";
      let ackUserName = "";
      let formattedDate = "";
      let formattedTime = "";

      if (isUnread) {
        const assignedToEntry = item.notificationAsgnTo?.find(
          (assign: any) => assign.assignedToUserId === userInfo.userId
        );
        if (assignedToEntry) {
          const formatted = formatAuditTimestamp(item.assignedToAuditTimeStamp);
          const [splitDate, splitTime] = assignedToEntry.assignedToAuditTimeStamp.split(" ");
          date = splitDate || "";
          time = splitTime + " " + assignedToEntry.assignedToAuditTimeStamp.split(" ")[2];
          formattedDate = formatted.date;
          formattedTime = formatted.time;
        } else {
          if (item.auditTimeStamp && item.auditTimeStamp != null) {
            const [splitDate, splitTime] = item.auditTimeStamp.split(" ");
            const formatted = formatAuditTimestamp(item.auditTimeStamp);
            date = splitDate || "";
            time = splitTime + " " + item.auditTimeStamp.split(" ")[2];
            formattedDate = formatted.date;
            formattedTime = formatted.time;
          }
        }
      } else {
        if (item.notificationAck.length > 0) {
          const ackEntry = item.notificationAck?.find(
            (ack: any) => ack.ackUsrId === userInfo.userId
          );

          if (ackEntry) {
            const [splitDate, splitTime] = ackEntry.ackAuditTimeStamp.split(" ");
            const formatted = formatAuditTimestamp(ackEntry.ackAuditTimeStamp);
            date = splitDate || "";
            time = splitTime + " " + ackEntry.ackAuditTimeStamp.split(" ")[2];
            formattedDate = formatted.date;
            formattedTime = formatted.time;
            ackUserName = ackEntry.ackUserName;
            if (ackEntry.ackUserEmail === userInfo.userName) {
              ackUserName = ackUserName + " (You)"
            }
          } else {
            const [splitDate, splitTime] = (item.notificationAck[0].ackAuditTimeStamp || "").split(" ");
            const formatted = formatAuditTimestamp(item.notificationAck[0].ackAuditTimeStamp);
            date = splitDate || "";
            time = splitTime + " " + (item.notificationAck[0].ackAuditTimeStamp || "").split(" ")[2];
            formattedDate = formatted.date;
            formattedTime = formatted.time;
            ackUserName = item.notificationAck[0].ackUserName;
            if (item.notificationAck[0].ackUserEmail === userInfo.userName) {
              ackUserName = ackUserName + " (You)"
            }
          }

          const [splitCreatedDate, splitCreatedTime] = item.auditTimeStamp.split(" ");
          const formatted = formatAuditTimestamp(item.auditTimeStamp);
          // createdDate = splitCreatedDate || "";
          // createdTime = splitCreatedTime + " " + item.auditTimeStamp.split(" ")[2];
          createdDate = formatted.date;
          createdTime = formatted.time;
        }
      }

      return {
        notification: item,
        notificationId: item.notificationId,
        entityId: item.entityId,
        entity: item.entity,
        title: item.notificationSub || "",
        description: item.notificationMsg || "",
        date,
        time,
        createdDate,
        createdTime,
        ackedByUserName: ackUserName,
        acknowledged: item.acknowledged || false,
        hasEntity: item.entity !== null && item.entityId !== null,
        isAssignable: (item.notificationType === "TO_USER" || (item.notificationType === "TO_ADMIN" && item.targetEntityId != 0)) ? true : false,
        attachments: item.notificationAttachment && item.notificationAttachment.length > 0 ? item.notificationAttachment : [],
        formattedDate,
        formattedTime
      };
    });

    setInboxData(mappedData);
  }, [NotificationListInfo]);


  const getLabel = (dateStr: string) => {
    const date = dayjs(dateStr);
    const today = dayjs();
    const yesterday = today.subtract(1, "day");

    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(yesterday, "day")) return "Yesterday";
    if (date.isAfter(today.subtract(7, "day"))) return "Last 7 days";
    if (date.month() === today.month() && date.year() === today.year())
      return "Earlier this month";

    return date.format("MMMM YYYY"); 
  };

  const grouped = inboxData?.reduce((acc, item) => {
    const label = getLabel(item.date);
    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {} as Record<string, NotificationData[]>);

  const sortPriority = [
    "Today",
    "Yesterday",
    "Last 7 days",
    "Earlier this month",
  ];

  const sortedGroups = Object.keys(grouped).sort((a, b) => {
    const aIndex = sortPriority.indexOf(a);
    const bIndex = sortPriority.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    } else if (aIndex !== -1) {
      return -1;
    } else if (bIndex !== -1) {
      return 1;
    } else {
      return dayjs(b).toDate().getTime() - dayjs(a).toDate().getTime();
    }
  });

  const sendingFunction = async (
    toValue: string,
    entity: number[],
    subject: string,
    message: string,
    important: boolean,
    attachments: Attachment[]) => {
    try {
      const composeData = {
        targetEntity: toValue ? toValue : "",
        targetEntityId: entity,
        subject: subject,
        notificationMsg: message,
        important: important,
        attachments: attachments
      }
      const response = await dispatch(composeNotification(composeData)).unwrap();
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    const currPage = newPage + 1;
    setCurrentPage(currPage);
  }

  const handleDeleteAction = async () => {

    {
      setShowOptionsPopup({
        message: "This will permanently delete all the acknowledged notifications. Do you really want to proceed? ",
        show: true,
        variant: "confirm",
        buttons: [{ name: "Confirm", color: 'primary', onClick: () => deleteAllSystemNotifications() }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }

  }

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  }

  const deleteAllSystemNotifications = async () => {
    cancelOptionPopup();
    try {
      const reqData = {
        unread: true,
        notificationCategory: "system"
      }
      const response = await dispatch(deleteAllNotifications()).unwrap();
      if (response.message === "SUCCESS") {
        refreshPage();
      }
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  }

  const handleAckAllAction = async () => {

    {
      setShowOptionsPopup({
        message: "Are you sure you want to acknowledge all unread notifications? This action will also mark these notifications as acknowledged for other users in your group as well.",
        show: true,
        variant: "confirm",
        buttons: [{
          name: "Confirm", color: 'primary', onClick: () => {
            ackAllNotifications();
          }
        }, { name: "Cancel", color: 'secondary', onClick: cancelOptionPopup }]
      })
    }

  }


  const ackAllNotifications = async () => {
    cancelOptionPopup();
    try {
      const reqData = {
        notificationTypes: accessibleNotifications ? accessibleNotifications : [],
        unread: true,
        notificationCategory: "system"
      }
      const response = await dispatch(acknowledgeAllNotifications(reqData)).unwrap();
      if (response.message === "SUCCESS") {
        refreshPage();
      }
      return response;
    } catch (err) {
      console.error("Failed to fetch wound codes:", err);
      return [];
    }
  }

  // unmount
  useEffect(
    () => () => {
      dispatch(resetNotificationData())
    },
    []
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
        pb: { xs: 6, sm: 4 },
      }}
    >
      {showOptionPopup.show &&
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      }

      <Box sx={{ mb: 2 }} data-testid="header">
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: mTokens.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            mb: 0.3,
          }}
        >
          Messages
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: "1.35rem", sm: "1.6rem" },
            fontWeight: 800,
            color: mTokens.textPrimary,
            lineHeight: 1.1,
          }}
        >
          Notifications
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={1.5}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Tabs value={tab} onChange={handleChange} sx={tabBarSx}>
          <Tab label="Unread" />
          <Tab label="Acknowledged" />
        </Tabs>

       <Box
          display="flex"
          gap={1.2}
          alignItems="center"
          justifyContent={{ xs: "space-between", sm: "flex-end" }}
          flexWrap="wrap"
        >
          {tab === 0 ? (
            <Button
              onClick={handleAckAllAction}
              variant="contained"
              disabled={!(totalElements > 0)}
              startIcon={<MarkChatRead />}
              removeUpperCase
              sx={{ ...primaryActionBtnSx, mx: 0.5 }}
            >
              Acknowledge All
            </Button>
          ) : (
            <Button
              onClick={handleDeleteAction}
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              disabled={!(totalElements > 0)}
              removeUpperCase
              sx={{ ...deleteAllBtnSx, mx: 0.5 }}
            >
              Delete All
            </Button>
          )}
          <Divider orientation="vertical" flexItem sx={{ borderColor: mTokens.border, my: 0.75, mx: 0.35 }} />
          <IconButton
            aria-label="close"
            onClick={() => navigate(-1)}
            sx={closeIconBtnSx}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      {NotificationListInfo && totalElements > 0 ? (
        <>
          {sortedGroups.map((group) => (
            <Box key={group} mb={2}>
              <Typography
                px={0.5}
                pb={1}
                sx={{
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  color: mTokens.textSecondary,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {group}
              </Typography>
              <Card sx={{ ...listCardSx, p: "0 !important", overflow: "hidden" }}>
                {grouped[group].map((item, index) => (
                  <NotificationItem
                    key={index}
                    {...item}
                    index={index}
                    total={grouped[group].length}
                    onAcknowledge={() => refreshPage()}
                    acknowledged={!isUnread}
                    showGoButton={item?.hasEntity}
                  />
                ))}
              </Card>
            </Box>
          ))}
          <Box
            sx={{
              ...listCardSx,
              p: { xs: 1.2, sm: 1.5 },
              mt: 1,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md>
                <Box display="flex" justifyContent="center">
                  <Pagination
                    variant="outlined"
                    shape="rounded"
                    count={totalPages || Math.ceil(totalElements / ITEMS_PER_PAGE)}
                    page={currentPage}
                    onChange={(event, value) => handleChangePage(event, value - 1)}
                    color={theme.palette.mode === "dark" ? "secondary" : "primary"}
                    showFirstButton
                    showLastButton
                    sx={paginationSx}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md="auto">
                <Box display="flex" justifyContent={{ xs: "center", md: "flex-end" }}>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: mTokens.textSecondary,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"Records: " + (totalElements ? totalElements : "0")}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </>
      ) : (
        <Card sx={{ ...listCardSx, p: 3, textAlign: "center", mt: 1 }}>
          <Typography sx={{ color: mTokens.textSecondary, fontWeight: 600, fontSize: "0.9rem" }}>
            No Notifications Found
          </Typography>
        </Card>
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        disablePortal
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              mt: 1,
              p: 2,
              border: `1px solid ${mTokens.border}`,
              boxShadow: isDark
                ? "0 16px 40px rgba(0,0,0,0.45)"
                : "0 16px 40px rgba(15,23,42,0.12)",
              borderRadius: "14px",
              backgroundColor: mTokens.bg,
              backgroundImage: "none",
              minWidth: 240,
              zIndex: 9999,
            }}
          >
            <Typography
              fontWeight={800}
              fontSize="0.86rem"
              mb={1}
              textAlign="center"
              color={mTokens.textPrimary}
            >
              Choose Recipient Type
            </Typography>
            <Box display="flex" gap={1.2}>
              <Button
                variant="contained"
                fullWidth
                removeUpperCase
                sx={primaryActionBtnSx}
                onClick={() => { setComposeMode("group"); setShowPopup(true); setAnchorEl(null); }}
              >
                Group
              </Button>
              <Button
                variant="contained"
                fullWidth
                removeUpperCase
                sx={primaryActionBtnSx}
                onClick={() => { setComposeMode("user"); setShowPopup(true); setAnchorEl(null); }}
              >
                User
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
      {showPopup && composeMode && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1300,
          }}
        >
        </Box>
      )}
    </Box>
  );
};

export default NotificationList;
